import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { useNavigate } from "react-router-dom";
import TablePaginationFooter from "components/common/TablePaginationFooter";

export default function TemplatesTable({
  templates,
  deleteTemplate,
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalElements,
}) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = (id) => {
    setTemplateToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplate(templateToDelete);
      setShowDeleteModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Standard truncation function used across all screens
  const formatValue = (val, length = 18) => (val && val.length > length ? `${val.slice(0, length)}...` : val);

const columns = [
  {
    accessorKey: "code",
    header: "Code",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const fullValue = getValue() || "None";
      return (
        <span 
          className="text-900 fw-normal" 
          title={fullValue} 
          style={{ fontSize: 13, cursor: 'help' }} // Standardized size
        >
          {formatValue(fullValue)}
        </span>
      );
    },
  },
  {
    accessorKey: "subject",
    header: "Subject",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const fullValue = getValue() || "No subject provided";
      return (
        <span
          className="text-500 d-inline-block fw-normal"
          style={{ fontSize: 13, cursor: 'help' }} // Standardized size
          title={fullValue}
        >
          {formatValue(fullValue, 25)}
        </span>
      );
    },
  },
  {
    accessorKey: "clientApp",
    header: "Client App",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const fullValue = getValue() || "N/A";
      return (
        <span
          className="d-inline-block fw-normal"
          style={{ color: "#343a40", fontSize: 13, cursor: 'help' }} // Standardized size
          title={fullValue}
        >
          {formatValue(fullValue, 15)}
        </span>
      );
    },
  },
  {
    accessorKey: "locale",
    header: "Locale",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => (
      <span className="text-500 fw-normal" style={{ fontSize: 13 }}>
        {getValue() || "N/A"}
      </span>
    ),
  },
  {
    accessorKey: "active",
    header: "Status",
    meta: {
      headerProps: { 
        style: { width: '100px' }, 
        className: "text-900 text-center fw-bold" // Bold Heading
      },
      cellProps: { className: "text-center" },
    },
    cell: ({ getValue }) => {
      const value = getValue();
      const style = {
        backgroundColor: value ? "#e8f7e3" : "#fbe9e7",
        color: value ? "#2e7d32" : "#c62828",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "0.7rem",
        lineHeight: 1.2,
        border: value ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
        cursor: "default",
        fontWeight: 600, // Kept status semi-bold
        display: "inline-block",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        textTransform: "uppercase"
      };
      const content = value === true ? "ACTIVE" : "DISABLED";
      return <span style={style}>{content}</span>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    meta: {
      headerProps: { className: "text-900 text-center fw-bold" },
      cellProps: { className: "text-center" },
    },
    cell: ({ row }) => {
      const item = row.original;
      return (
        <CardDropdown drop="start">
          <div className="py-2 fw-normal"> {/* Standardized Menu Weight */}
            <Guard privilege={PRIV.VIEW_FEEDBACKS}>
              <Dropdown.Item onClick={() => navigate(`/account/message-templates/${item.id}`)}>
                View
              </Dropdown.Item>
            </Guard>
            <Guard privilege={PRIV.VIEW_FEEDBACKS}>
              <Dropdown.Item onClick={() => navigate(`/account/message-templates/update/${item.id}`)}>
                Edit
              </Dropdown.Item>
            </Guard>
            <Guard privilege={PRIV.VIEW_FEEDBACKS}>
              <Dropdown.Divider as="div" />
              <Dropdown.Item className="text-danger" onClick={() => confirmDelete(item.id)}>
                Delete
              </Dropdown.Item>
            </Guard>
          </div>
        </CardDropdown>
      );
    },
  },
];

  const table = useAdvanceTable({
    data: templates,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="mx-n1 px-1">
          <AdvanceTable
            headerClassName="bg-light text-nowrap align-middle text-dark fw-bold" // Bold headers
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden fw-normal", // Normal rows
            }}
          />
        </div>
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPrev={page > 0 ? () => setPage(page - 1) : undefined}
            onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="template"
        loading={isDeleting}
      />
    </>
  );
}